/* Program Name: AlertContext.js
 **  Description: a component that handles the state of the alert messages for the
 **               entire application
 **
 ** Project    Date        Who  Comments
 ** ---------  ----------  ---  ---------------------------------------------------
 ** IS0200241  2021-04-12  STT  Initial program
 *********************************************************************************/

/**React imports */
import React, { useContext, createContext, useState } from 'react'

import { useSession } from './SessionContext'

//Creates the AlertContext using reacts context //DOCS: https://reactjs.org/docs/context.html
const AlertContext = createContext()

//Creates the hook to access the state of the AlertContext //DOCS: https://reactjs.org/docs/hooks-custom.html
export function useAlert() {
  return useContext(AlertContext)
}

/**Constructor for the AlertProvider component */
//children > props that passes the components through it like a wrapper
export default function AlertProvider({ children }) {
  /*********************************************************************************************** */
  /**Local Variables */

  const { handleLogout } = useSession()

  //alert holds the status of the alerts 'success' and 'error'
  //false = closed, true = open
  const [alert, setAlert] = useState({
    success: false,
    error: false,
    warning: false,
  })

  //message contains the text that will be displayed on the alert
  const [message, setMessage] = useState(null)

  const [siteNotify, setSiteNotify] = useState(null)

  /*********************************************************************************************** */
  /**Local Functions */

  // allAlertsOff closes all alerts
  const allAlertsOff = () => {
    setAlert({
      success: false,
      error: false,
      warning: false,
    })
  }

  //errorMesssage takes an input of a message and toggles an error alert that dispalys the message
  const errorMessage = (message) => {
    setMessage(message)
    setAlert({
      success: false,
      error: true,
      warning: false,
    })
  }

  //successMesssage takes an input of a message and toggles an success alert that dispalys the message
  const successMessage = (message) => {
    setMessage(message)
    setAlert({
      success: true,
      error: false,
      warning: false,
    })
  }

  //warningMesssage takes an input of a message and toggles an warning alert that dispalys the message
  const warningMessage = (message) => {
    setMessage(message)
    setAlert({
      success: false,
      error: false,
      warning: true,
    })
  }

  const closeSiteNote = () => {
    setSiteNotify(null)
  }

  //postAjaxAlert takes the meta object returned from the ajax call and an action if nessacary and returns the
  //approriate message on the appropriate alert, the action can be used to describe what action was successful
  const postAjaxAlert = (meta, action) => {
    if (
      meta.errcode === 'invalid_login' ||
      meta.errcode === 'Invalid_Session'
    ) {
      errorMessage('Invalid Session. Please Sign In Again')
      handleLogout()
      return false
    }

    if (meta.err) {
      errorMessage(meta.errmsg + ' Correlation ID: ' + meta.corr)
    } else {
      if (meta.warn) {
        warningMessage(meta.errmsg)
      } else {
        if (action !== 'rdr') {
          successMessage(action ? action + ' Successful' : '')
        }
      }
    }
    if (meta.metaSiteMsg) {
      setSiteNotify(meta.metaSiteMsg[0])
    }
  }

  const siteNotification = (msg) => {
    setSiteNotify(msg)
  }

  //value is the object that is passed into the Provider which allows anything in the value to be access
  //by the children of AlertProvider
  const value = {
    alert,
    message,
    postAjaxAlert,
    errorMessage,
    warningMessage,
    successMessage,
    siteNotify,
    closeSiteNote,
    allAlertsOff,
    siteNotification,
  }

  /*********************************************************************************************** */
  /**Rendering */

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
}
